/**
 * `POST /api/assets` (`040-api-contract.md`, `062-images-assets.md` §3).
 *
 * **Why this is a direct `fetch` and not an outbox entry.** Everything else in the app
 * routes writes through `outbox.enqueue()` (`112-offline-drafts.md` §5), and that rule is
 * not being bent here. The outbox carries *JSON* requests; a 3 MB screenshot is not JSON,
 * and base64-ing it into an outbox row would triple it and put binary in a queue built for
 * document patches. `112` §8 gives image bytes their own durable store instead —
 * `pendingUploads`, holding the real `Blob`, with its own retry — and it is that store, not
 * this function, that carries the never-lose-data guarantee. A failed upload here is caught
 * and written there (`offline/uploads.ts`). No byte is ever dropped on the floor.
 */
import { apiFetch } from '@/api/client';

export interface AssetResponse {
  id: string;
  /** Always a relative `/uploads/…` path. Never absolute, never `data:` (`031`). */
  url: string;
  width: number;
  height: number;
  mimeType: string;
  sizeBytes: number;
}

/** `062` §2. The client checks first so the common mistake never round-trips. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export const ALLOWED_MIME = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/avif',
] as const;

/**
 * **The status code is deliberately not returned, because the client must not branch on it.**
 *
 * `201` = newly stored, `200` = a dedupe hit on the sha256 (`062` §3.4.6). The body is
 * identical in both cases and the distinction exists purely so the server's behaviour is
 * honest and observable in a log. A client that treated a `200` differently would be
 * inventing a difference the contract does not have.
 */
export async function uploadAsset(file: File, pageId: string): Promise<AssetResponse> {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('pageId', pageId);

  // No `Content-Type` header: `FormData` sets its own multipart boundary, and overriding it
  // produces a body the server cannot parse. `apiFetch` knows this and leaves it alone.
  return apiFetch<AssetResponse>('/api/assets', { method: 'POST', body: form });
}
